import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Button, TextField, Paper, Alert, Rating,
} from '@mui/material';
import { Star } from '@mui/icons-material';
import { campaignRatingService } from '../services/api';
import { BRAND, COLORS, primaryButtonSx, bodyTextSx, cardHeadingSx, sectionSubtitleSx, sectionHeadingSx } from '../theme/pageStyles';

const emptyForm = {
  full_name: '',
  email: '',
  phone: '',
  rating: 0,
  review: '',
};

function extractRatingsPayload(res) {
  const body = res?.data;
  const list = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
  const summary = body?.summary || { average: 0, count: list.length };
  return { list, summary };
}

export default function CampaignRatingsSection({ campaignId, initialRatings, initialSummary }) {
  const [ratings, setRatings] = useState(Array.isArray(initialRatings) ? initialRatings : []);
  const [summary, setSummary] = useState(initialSummary || { average: 0, count: 0 });
  const [loading, setLoading] = useState(!Array.isArray(initialRatings));
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [submitError, setSubmitError] = useState('');

  const loadRatings = async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const res = await campaignRatingService.getApprovedByCampaign(campaignId);
      const payload = extractRatingsPayload(res);
      setRatings(payload.list);
      setSummary({
        average: Number(payload.summary?.average) || 0,
        count: Number(payload.summary?.count) || payload.list.length,
      });
    } catch (e) {
      if (!Array.isArray(initialRatings)) {
        setRatings([]);
        setSummary({ average: 0, count: 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.full_name.trim()) next.full_name = 'Full name is required.';
    if (!form.email.trim()) next.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Please enter a valid email address.';
    if (!form.phone.trim()) next.phone = 'Phone number is required.';
    else if (form.phone.replace(/\D/g, '').length < 10) next.phone = 'Please enter a valid phone number (at least 10 digits).';
    if (!form.rating || form.rating < 1) next.rating = 'Please choose a rating from 1 to 5.';
    if (!form.review.trim()) next.review = 'Review is required.';
    else if (form.review.trim().length < 10) next.review = 'Please write a short review (at least 10 characters).';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setSubmitError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      await campaignRatingService.submitRating(campaignId, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        rating: Number(form.rating),
        review: form.review.trim(),
      });
      setForm(emptyForm);
      setSuccessMsg('Thank you. Your rating was submitted and will appear here after admin approval.');
    } catch (err) {
      setSubmitError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Could not submit your rating. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const avg = Number(summary.average) || 0;
  const count = Number(summary.count) || ratings.length;

  return (
    <Box sx={{ width: '100%' }}>
      {loading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: BRAND.green }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box>
              <Typography sx={{ ...sectionSubtitleSx, mb: 0.5 }}>Community feedback</Typography>
              <Typography sx={{ ...sectionHeadingSx, fontSize: { xs: 22, md: 28 } }}>Ratings</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { sm: 'auto' } }}>
              <Rating value={avg} precision={0.1} readOnly sx={{ color: BRAND.green }} />
              <Typography sx={{ ...bodyTextSx, fontWeight: 700 }}>
                {count > 0 ? `${avg.toFixed(1)} / 5` : 'No ratings yet'}
              </Typography>
              {count > 0 && (
                <Typography sx={{ ...bodyTextSx, color: 'text.secondary' }}>
                  ({count} review{count === 1 ? '' : 's'})
                </Typography>
              )}
            </Box>
          </Box>

          {ratings.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', backgroundColor: COLORS.cream, borderRadius: 2, px: 3, mb: 4, width: '100%' }}>
              <Typography sx={{ ...sectionSubtitleSx, color: COLORS.subtitle }}>No published reviews yet</Typography>
              <Typography sx={{ ...bodyTextSx, mt: 1, color: COLORS.body }}>
                Be the first to rate this campaign. Approved reviews will appear here.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                mb: 4,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e8e0d0',
                backgroundColor: '#fff',
              }}
            >
              {ratings.map((r, idx) => {
                const date = r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                return (
                  <Box
                    key={r.id}
                    sx={{
                      width: '100%',
                      p: { xs: 2, sm: 2.5 },
                      backgroundColor: COLORS.warm,
                      borderLeft: `4px solid ${BRAND.green}`,
                      borderBottom: idx < ratings.length - 1 ? '1px solid #e8e0d0' : 'none',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: BRAND.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Star sx={{ color: '#fff' }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography sx={{ ...cardHeadingSx, fontSize: 17, mb: 0 }}>
                            {r.full_name || 'Supporter'}
                          </Typography>
                          {date && (
                            <Typography sx={{ ...bodyTextSx, fontSize: 12, color: 'text.secondary' }}>{date}</Typography>
                          )}
                        </Box>
                        <Rating value={Number(r.rating) || 0} readOnly size="small" sx={{ color: BRAND.green, mb: 0.75 }} />
                        <Typography sx={{ ...bodyTextSx, fontSize: 14, fontStyle: 'italic' }}>
                          “{r.review}”
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      )}

      <Paper
        elevation={0}
        component="form"
        onSubmit={handleSubmit}
        sx={{ width: '100%', p: { xs: 2, sm: 3 }, borderRadius: 2, border: '1px solid #e8e0d0', backgroundColor: '#F9F4E8' }}
      >
        <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 800, fontSize: 18, color: '#2D2929', mb: 0.5 }}>
          Rate this campaign
        </Typography>
        <Typography sx={{ ...bodyTextSx, fontSize: 14, color: 'text.secondary', mb: 2 }}>
          Share your experience. Reviews are published after admin approval.
        </Typography>

        {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Full Name *" name="full_name" value={form.full_name} onChange={handleChange} error={!!errors.full_name} helperText={errors.full_name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email *" name="email" type="email" value={form.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Phone Number *" name="phone" type="tel" value={form.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone} placeholder="+91 XXXXX XXXXX" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography sx={{ ...bodyTextSx, mb: 0.5 }}>Rating *</Typography>
              <Rating
                name="campaign-rating"
                value={Number(form.rating) || 0}
                onChange={(_, value) => {
                  setForm((prev) => ({ ...prev, rating: value || 0 }));
                  if (errors.rating) setErrors((prev) => ({ ...prev, rating: '' }));
                }}
                sx={{ color: BRAND.green }}
              />
              {errors.rating && (
                <Typography sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>{errors.rating}</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Review *"
              name="review"
              value={form.review}
              onChange={handleChange}
              error={!!errors.review}
              helperText={errors.review}
              multiline
              rows={3}
              placeholder="Tell others about this campaign..."
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" disabled={submitting} sx={{ ...primaryButtonSx, backgroundColor: BRAND.green }}>
            {submitting ? 'Submitting…' : 'Submit rating'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
